import React, {
  useState,
  useRef,
  useMemo,
  useCallback,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { Arrow, General, Time } from 'icons/untitledui';
import dayjs from 'shared/helpers/dayjs';
import useClickOutside from 'shared/hooks/useClickOutside';
import { usePosthogCapture } from 'dashboard/posthogCapture/_index';

import * as Styled from 'dashboard/styles/Filters.style';
import { calendarPopperSx } from 'dashboard/styles/Filters.style';
import * as Text from 'theme/components/Text.style';

type PresetKey = 'today' | 'yesterday' | 'last7' | 'last30' | 'last90';

const PRESETS: {
  key: PresetKey;
  labelKey: string;
  getRange: () => { from: Date; to: Date };
}[] = [
  {
    key: 'today',
    labelKey: 'filters.today',
    getRange: () => {
      const d = dayjs().startOf('day').toDate();
      return { from: d, to: d };
    },
  },
  {
    key: 'yesterday',
    labelKey: 'filters.yesterday',
    getRange: () => {
      const d = dayjs().subtract(1, 'day').startOf('day').toDate();
      return { from: d, to: d };
    },
  },
  {
    key: 'last7',
    labelKey: 'filters.last7Days',
    getRange: () => ({
      from: dayjs().subtract(6, 'day').startOf('day').toDate(),
      to: dayjs().startOf('day').toDate(),
    }),
  },
  {
    key: 'last30',
    labelKey: 'filters.last30Days',
    getRange: () => ({
      from: dayjs().subtract(29, 'day').startOf('day').toDate(),
      to: dayjs().startOf('day').toDate(),
    }),
  },
  {
    key: 'last90',
    labelKey: 'filters.last90Days',
    getRange: () => ({
      from: dayjs().subtract(89, 'day').startOf('day').toDate(),
      to: dayjs().startOf('day').toDate(),
    }),
  },
];

function detectPreset(from: Date | null, to: Date | null): PresetKey | null {
  if (!from || !to) return null;
  const f = dayjs(from).startOf('day');
  const t = dayjs(to).startOf('day');
  const match = PRESETS.find((preset) => {
    const range = preset.getRange();
    return (
      f.isSame(dayjs(range.from), 'day') && t.isSame(dayjs(range.to), 'day')
    );
  });
  return match?.key ?? null;
}

interface DateRangeFilterProps {
  fromDate: string;
  toDate: string;
  onDateRangeChange: (from: string, to: string) => void;
}

export default function DateRangeFilter({
  fromDate,
  toDate,
  onDateRangeChange,
}: DateRangeFilterProps): JSX.Element {
  const { t } = useTranslation('pageDashboard');
  const dashboard = usePosthogCapture();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const muiPopperRef = useRef<HTMLDivElement>(null);

  const [localFrom, setLocalFrom] = useState<Date | null>(
    new Date(`${fromDate}T00:00:00`)
  );
  const [localTo, setLocalTo] = useState<Date | null>(
    new Date(`${toDate}T00:00:00`)
  );
  const [fromPickerOpen, setFromPickerOpen] = useState(false);
  const [toPickerOpen, setToPickerOpen] = useState(false);

  const activePreset = useMemo(
    () => detectPreset(localFrom, localTo),
    [localFrom, localTo]
  );

  const handleClose = useCallback(() => {
    setOpen(false);
    if (localFrom && localTo) {
      const isCustom = !detectPreset(localFrom, localTo);
      onDateRangeChange(
        dayjs(localFrom).format('YYYY-MM-DD'),
        dayjs(localTo).format('YYYY-MM-DD')
      );
      if (isCustom) {
        dashboard.dateRangeCustomApplied();
      }
    }
  }, [localFrom, localTo, onDateRangeChange, dashboard]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('.MuiPickersPopper-root')) {
        e.stopPropagation();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    return () =>
      document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [open]);

  useClickOutside([anchorRef, popoverRef, muiPopperRef], handleClose, open);

  const displayText = useMemo(() => {
    const from = localFrom ? dayjs(localFrom) : dayjs(fromDate);
    const to = localTo ? dayjs(localTo) : dayjs(toDate);
    const diffDays = to.diff(from, 'day');

    if (to.isSame(dayjs(), 'day') && diffDays > 0) {
      return t('filters.previousDays', { count: diffDays + 1 });
    }

    return `${from.format('MMM D')} – ${to.format('MMM D')}`;
  }, [localFrom, localTo, fromDate, toDate, t]);

  const handleToggle = () => setOpen((prev) => !prev);

  const handlePresetClick = useCallback(
    (preset: (typeof PRESETS)[number]) => {
      const { from, to } = preset.getRange();
      setLocalFrom(from);
      setLocalTo(to);
      onDateRangeChange(
        dayjs(from).format('YYYY-MM-DD'),
        dayjs(to).format('YYYY-MM-DD')
      );
      dashboard.dateRangePresetSelected({ preset: preset.key });
    },
    [onDateRangeChange, dashboard]
  );

  return (
    <Styled.FilterWrapper>
      <Styled.DateRangeButton
        ref={anchorRef}
        onClick={handleToggle}
        aria-label={displayText}
        $open={open}
      >
        <span>{displayText}</span>
        <Time.Calendar size={20} />
      </Styled.DateRangeButton>

      {open && (
        <Styled.DateRangePopover ref={popoverRef}>
          <Styled.PresetList>
            {PRESETS.map((preset) => {
              const selected = activePreset === preset.key;
              return (
                <Styled.Option
                  key={preset.key}
                  $selected={selected}
                  onClick={() => handlePresetClick(preset)}
                >
                  <span>{t(preset.labelKey)}</span>
                  {selected && (
                    <Styled.OptionCheck>
                      <General.Check size={12} />
                    </Styled.OptionCheck>
                  )}
                </Styled.Option>
              );
            })}
          </Styled.PresetList>
          <Styled.DateFieldList>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Styled.DateFieldRow>
                <Styled.DateFieldContent>
                  <Text.Label>{t('filters.from')}</Text.Label>
                  <DesktopDatePicker
                    value={localFrom}
                    onChange={setLocalFrom}
                    open={fromPickerOpen}
                    onOpen={() => setFromPickerOpen(true)}
                    onClose={() => setFromPickerOpen(false)}
                    views={['day']}
                    format="MM/dd/yyyy"
                    maxDate={localTo || undefined}
                    slots={{
                      leftArrowIcon: Arrow.ChevronLeft,
                      rightArrowIcon: Arrow.ChevronRight,
                    }}
                    slotProps={{
                      textField: { size: 'small', fullWidth: true },
                      popper: { ref: muiPopperRef, sx: calendarPopperSx },
                    }}
                  />
                </Styled.DateFieldContent>
                <Styled.DateFieldIcon
                  type="button"
                  onClick={() => setFromPickerOpen(true)}
                  aria-label={t('filters.from')}
                >
                  <Time.Calendar size={20} />
                </Styled.DateFieldIcon>
              </Styled.DateFieldRow>
              <Styled.DateFieldRow>
                <Styled.DateFieldContent>
                  <Text.Label>{t('filters.to')}</Text.Label>
                  <DesktopDatePicker
                    value={localTo}
                    onChange={setLocalTo}
                    open={toPickerOpen}
                    onOpen={() => setToPickerOpen(true)}
                    onClose={() => setToPickerOpen(false)}
                    views={['day']}
                    format="MM/dd/yyyy"
                    minDate={localFrom || undefined}
                    maxDate={new Date()}
                    slots={{
                      leftArrowIcon: Arrow.ChevronLeft,
                      rightArrowIcon: Arrow.ChevronRight,
                    }}
                    slotProps={{
                      textField: { size: 'small', fullWidth: true },
                      popper: { ref: muiPopperRef, sx: calendarPopperSx },
                    }}
                  />
                </Styled.DateFieldContent>
                <Styled.DateFieldIcon
                  type="button"
                  onClick={() => setToPickerOpen(true)}
                  aria-label={t('filters.to')}
                >
                  <Time.Calendar size={20} />
                </Styled.DateFieldIcon>
              </Styled.DateFieldRow>
            </LocalizationProvider>
          </Styled.DateFieldList>
        </Styled.DateRangePopover>
      )}
    </Styled.FilterWrapper>
  );
}
